import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import SearchableBankSelect from '../components/SearchableBankSelect';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import api from '../lib/api';
import { Plus, Eye, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ExportMenu } from '../components/ExportMenu';
import { ImportDialog } from '../components/ImportDialog';
import { CategoryBadge, CategoryFilter, CategoryPicker } from '../components/CategoryControls';
import { lockedCompanyCategory } from '../utils/requestTheme';


export default function Companys() {
  const { user } = useAuth();
  const [companys, setCompanys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFilter, setSearchFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');
  // Purchasing/Gudang/Clerk/AP: hanya perusahaan IPR (server juga menolak OPR).
  const lockedCat = lockedCompanyCategory(user);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    nama_perusahaan: '',
    alamat_lengkap: '',
    nomor_npwp: '',
    nama_bank: '',
    nomor_rekening: '',
    nama_pemilik_rekening: '',
    email: '',
    nomor_wa: '',
    nomor_kontak_person: '',
    nama_kontak_person: '',
    ttd_fat_nama: '',
    ttd_fat_jabatan: '',
    ttd_gm_nama: '',
    ttd_gm_jabatan: '',
    ttd_finance_nama: '',
    ttd_finance_jabatan: '',
    ttd_pemeriksa_nama: '',
    ttd_pemeriksa_jabatan: '',
    category: '',
  });

  // Helper function to update form data with uppercase (except email)
  const updateFormField = (field, value) => {
    if (field === 'email') {
      setFormData({ ...formData, [field]: value.toLowerCase() });
    } else if (field.startsWith('ttd_') || field === 'nomor_rekening' || field === 'nomor_kontak_person' || field === 'nomor_wa' || field === 'nomor_npwp') {
      // Keep numbers as-is
      setFormData({ ...formData, [field]: value });
    } else {
      setFormData({ ...formData, [field]: value.toUpperCase() });
    }
  };

  useEffect(() => {
    fetchCompanys();
  }, []);

  const fetchCompanys = async () => {
    try {
      const response = await api.get(`/companies`);
      setCompanys(response.data);
    } catch (error) {
      console.error('Failed to fetch companys:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to extract error message from API response
  const getErrorMessage = (error, defaultMsg) => {
    const detail = error.response?.data?.detail;
    if (!detail) return defaultMsg;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    if (typeof detail === 'object') {
      return detail.msg || detail.message || JSON.stringify(detail);
    }
    return defaultMsg;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const body = lockedCat ? { ...formData, category: lockedCat } : formData;
    if (!body.category) return toast.error('Pilih kategori IPR atau OPR');
    try {
      await api.post(`/companies`, body);
      toast.success('Company berhasil ditambahkan!');
      setShowCreate(false);
      resetForm();
      fetchCompanys();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Gagal menambahkan company'));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const body = lockedCat ? { ...formData, category: lockedCat } : formData;
    if (!body.category) return toast.error('Pilih kategori IPR atau OPR');
    try {
      await api.put(`/companies/${selectedCompany.id}`, body);
      toast.success('Company berhasil diupdate!');
      setShowEdit(false);
      resetForm();
      fetchCompanys();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Gagal update company'));
    }
  };

  const handleDelete = async (companyId) => {
    if (!window.confirm('Yakin ingin menghapus company ini?')) return;
    try {
      await api.delete(`/companies/${companyId}`);
      toast.success('Company berhasil dihapus!');
      fetchCompanys();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Gagal menghapus company'));
    }
  };

  const resetForm = () => {
    setFormData({
      nama_perusahaan: '',
      alamat_lengkap: '',
      nomor_npwp: '',
      nama_bank: '',
      nomor_rekening: '',
      nama_pemilik_rekening: '',
      email: '',
      nomor_wa: '',
      nomor_kontak_person: '',
      nama_kontak_person: '',
      ttd_fat_nama: '',
      ttd_fat_jabatan: '',
      ttd_gm_nama: '',
      ttd_gm_jabatan: '',
      ttd_finance_nama: '',
      ttd_finance_jabatan: '',
      ttd_pemeriksa_nama: '',
      ttd_pemeriksa_jabatan: '',
      category: '',
    });
  };

  const openEdit = (company) => {
    setSelectedCompany(company);
    setFormData({
      nama_perusahaan: company.nama_perusahaan,
      alamat_lengkap: company.alamat_lengkap,
      nomor_npwp: company.nomor_npwp,
      nama_bank: company.nama_bank || '',
      nomor_rekening: company.nomor_rekening,
      nama_pemilik_rekening: company.nama_pemilik_rekening,
      email: company.email,
      nomor_wa: company.nomor_wa || '',
      nomor_kontak_person: company.nomor_kontak_person || '',
      nama_kontak_person: company.nama_kontak_person || '',
      ttd_fat_nama: company.ttd_fat_nama || '',
      ttd_fat_jabatan: company.ttd_fat_jabatan || '',
      ttd_gm_nama: company.ttd_gm_nama || '',
      ttd_gm_jabatan: company.ttd_gm_jabatan || '',
      ttd_finance_nama: company.ttd_finance_nama || '',
      ttd_finance_jabatan: company.ttd_finance_jabatan || '',
      ttd_pemeriksa_nama: company.ttd_pemeriksa_nama || '',
      ttd_pemeriksa_jabatan: company.ttd_pemeriksa_jabatan || '',
      category: company.category || 'ipr',
    });
    setShowEdit(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="companys-page">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Database Perusahaan</h1>
          <div className="flex gap-2">
            <ExportMenu table="companies" size="default" params={{ category: catFilter }} />
            {['purchasing', 'admin'].includes(user?.role) && <ImportDialog type="companies" title="Perusahaan" size="default" onDone={fetchCompanys} />}
            <Button
              variant="outline"
              onClick={fetchCompanys}
              data-testid="refresh-companies-button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/></svg>
              Refresh
            </Button>
            {['purchasing', 'admin'].includes(user?.role) && (
              <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button data-testid="create-company-button" className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="mr-2 h-4 w-4" /> Tambah Company
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Tambah Company Baru</DialogTitle>
                  <p className="text-sm text-slate-600">Semua field wajib diisi</p>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nama_perusahaan">Nama Company *</Label>
                      <Input
                        id="nama_perusahaan"
                        value={formData.nama_perusahaan}
                        onChange={(e) => updateFormField('nama_perusahaan', e.target.value)}
                        style={{ textTransform: 'uppercase' }}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => updateFormField('email', e.target.value)}
                        style={{ textTransform: 'lowercase' }}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="nomor_wa">Nomor WhatsApp</Label>
                    <Input
                      id="nomor_wa"
                      type="tel"
                      pattern="[0-9]{10,15}"
                      maxLength="15"
                      value={formData.nomor_wa}
                      onChange={(e) => updateFormField('nomor_wa', e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="6281234567890"
                    />
                    <p className="text-xs text-slate-500">Format: 62 + nomor (contoh: 6281234567890)</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="alamat">Alamat Lengkap *</Label>
                    <textarea
                      id="alamat"
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
                      value={formData.alamat_lengkap}
                      onChange={(e) => updateFormField('alamat_lengkap', e.target.value)}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="npwp">Nomor NPWP *</Label>
                      <Input
                        id="npwp"
                        value={formData.nomor_npwp}
                        onChange={(e) => updateFormField('nomor_npwp', e.target.value)}
                        required
                      />
                    </div>
                    <SearchableBankSelect
                      value={formData.nama_bank}
                      onChange={(bankName) => setFormData({ ...formData, nama_bank: bankName })}
                      label="Nama Bank"
                      required={true}
                      id="bank"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="rekening">Nomor Rekening *</Label>
                      <Input
                        id="rekening"
                        value={formData.nomor_rekening}
                        onChange={(e) => updateFormField('nomor_rekening', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pemilik">Nama Pemilik Rekening *</Label>
                      <Input
                        id="pemilik"
                        value={formData.nama_pemilik_rekening}
                        onChange={(e) => updateFormField('nama_pemilik_rekening', e.target.value)}
                        style={{ textTransform: 'uppercase' }}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nomor_kontak">Nomor Kontak Person * (10-13 digit)</Label>
                      <Input
                        id="nomor_kontak"
                        type="tel"
                        pattern="[0-9]{10,13}"
                        maxLength="13"
                        value={formData.nomor_kontak_person}
                        onChange={(e) => updateFormField('nomor_kontak_person', e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="08123456789"
                        required
                      />
                      <p className="text-xs text-slate-500">Min 10 digit, Max 13 digit</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="nama_kontak">Nama Kontak Person *</Label>
                      <Input
                        id="nama_kontak"
                        type="text"
                        value={formData.nama_kontak_person}
                        onChange={(e) => updateFormField('nama_kontak_person', e.target.value)}
                        style={{ textTransform: 'uppercase' }}
                        placeholder="JOHN DOE"
                        required
                      />
                    </div>
                  </div>


                <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
                  <div>
                    <p className="font-medium text-slate-800">Penanda Tangan Cetakan</p>
                    <p className="text-xs text-slate-500">
                      Nama ini dibaca langsung dari master saat mencetak, sehingga pergantian pejabat
                      langsung berlaku — termasuk pada dokumen lama yang dicetak ulang.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="ttd_fat_nama">FAT Manager — Nama</Label>
                      <Input
                        id="ttd_fat_nama"
                        value={formData.ttd_fat_nama}
                        onChange={(e) => updateFormField('ttd_fat_nama', e.target.value)}
                        placeholder="Kosongkan bila cukup jabatan"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="ttd_fat_jabatan">FAT Manager — Jabatan</Label>
                      <Input
                        id="ttd_fat_jabatan"
                        value={formData.ttd_fat_jabatan}
                        onChange={(e) => updateFormField('ttd_fat_jabatan', e.target.value)}
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-500 -mt-1">Cetak PO kolom “Diketahui Oleh”, Cetak Invoice kolom “Disetujui Oleh”</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="ttd_gm_nama">General Manager — Nama</Label>
                      <Input
                        id="ttd_gm_nama"
                        value={formData.ttd_gm_nama}
                        onChange={(e) => updateFormField('ttd_gm_nama', e.target.value)}
                        placeholder="Kosongkan bila cukup jabatan"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="ttd_gm_jabatan">General Manager — Jabatan</Label>
                      <Input
                        id="ttd_gm_jabatan"
                        value={formData.ttd_gm_jabatan}
                        onChange={(e) => updateFormField('ttd_gm_jabatan', e.target.value)}
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-500 -mt-1">Cetak PO kolom “Disetujui Oleh”</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="ttd_pemeriksa_nama">Pemeriksa — Nama</Label>
                      <Input
                        id="ttd_pemeriksa_nama"
                        value={formData.ttd_pemeriksa_nama}
                        onChange={(e) => updateFormField('ttd_pemeriksa_nama', e.target.value)}
                        placeholder="Kosongkan bila cukup jabatan"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="ttd_pemeriksa_jabatan">Pemeriksa — Jabatan</Label>
                      <Input
                        id="ttd_pemeriksa_jabatan"
                        value={formData.ttd_pemeriksa_jabatan}
                        onChange={(e) => updateFormField('ttd_pemeriksa_jabatan', e.target.value)}
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-500 -mt-1">Voucher Pembayaran kolom “Diperiksa Oleh”</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="ttd_finance_nama">Finance — Nama</Label>
                      <Input
                        id="ttd_finance_nama"
                        value={formData.ttd_finance_nama}
                        onChange={(e) => updateFormField('ttd_finance_nama', e.target.value)}
                        placeholder="Kosongkan bila cukup jabatan"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="ttd_finance_jabatan">Finance — Jabatan</Label>
                      <Input
                        id="ttd_finance_jabatan"
                        value={formData.ttd_finance_jabatan}
                        onChange={(e) => updateFormField('ttd_finance_jabatan', e.target.value)}
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-500 -mt-1">Voucher Pembayaran kolom “Dibayar Oleh”</p>
                  </div>
                </div>

                <CategoryPicker value={formData.category} onChange={(c) => setFormData({ ...formData, category: c })} locked={lockedCat} testId="company-category" />
                <p className="text-xs text-slate-500 -mt-2">Kategori menentukan perusahaan muncul di form IPR atau OPR. PO tetap bisa memakai semua perusahaan.</p>

                  <Button type="submit" className="w-full" data-testid="submit-company-button">
                    Simpan Company
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Daftar Company</CardTitle>
            <div className="flex flex-wrap items-center gap-3">
            <CategoryFilter value={catFilter} onChange={setCatFilter} locked={lockedCat} testId="company-category-filter" />
            <Input
              placeholder="Cari nama company..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              className="max-w-xs"
              data-testid="search-company"
            />
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const sortKey = (name) => {
                const n = (name || '').toUpperCase();
                return n.replace(/^(PT\.?\s*|CV\.?\s*)/i, '').trim();
              };
              const sorted = [...companys].sort((a, b) => sortKey(a.nama_perusahaan).localeCompare(sortKey(b.nama_perusahaan)));
              const filtered = sorted.filter(c => (!searchFilter || c.nama_perusahaan?.toLowerCase().includes(searchFilter.toLowerCase()))
                && (!catFilter || (c.category || 'ipr') === catFilter));
              return filtered.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-3 text-sm font-semibold">Nama Company</th>
                      <th className="px-4 py-3 text-sm font-semibold">Email</th>
                      <th className="px-4 py-3 text-sm font-semibold">Bank</th>
                      <th className="px-4 py-3 text-sm font-semibold">Nama Kontak</th>
                      <th className="px-4 py-3 text-sm font-semibold">Kategori</th>
                      <th className="px-4 py-3 text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((company) => (
                      <tr key={company.id} className="border-b">
                        <td className="px-4 py-3 font-semibold">{company.nama_perusahaan}</td>
                        <td className="px-4 py-3">{company.email}</td>
                        <td className="px-4 py-3 text-sm">{company.nama_bank || '-'}</td>
                        <td className="px-4 py-3">{company.nama_kontak_person || '-'}</td>
                        <td className="px-4 py-3"><CategoryBadge value={company.category} testId={`company-category-${company.id}`} /></td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setSelectedCompany(company); setShowDetail(true); }}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {['purchasing', 'admin'].includes(user?.role) && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => openEdit(company)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleDelete(company.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">{searchFilter ? 'Tidak ada hasil pencarian' : 'Belum ada company'}</p>
            );
            })()}
          </CardContent>
        </Card>

        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Detail Company</DialogTitle>
              <p className="text-sm text-slate-600">Informasi lengkap company</p>
            </DialogHeader>
            {selectedCompany && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Nama Company</p>
                    <p className="font-semibold">{selectedCompany.nama_perusahaan}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="font-semibold">{selectedCompany.email}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-slate-600">Alamat Lengkap</p>
                    <p className="text-sm">{selectedCompany.alamat_lengkap}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Nomor NPWP</p>
                    <p className="font-mono">{selectedCompany.nomor_npwp}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Bank</p>
                    <p className="font-semibold">{selectedCompany.nama_bank || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Nomor Rekening</p>
                    <p className="font-mono">{selectedCompany.nomor_rekening}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Nama Pemilik Rekening</p>
                    <p>{selectedCompany.nama_pemilik_rekening}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Nomor Kontak Person</p>
                    <p className="font-mono">{selectedCompany.nomor_kontak_person || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Nama Kontak Person</p>
                    <p>{selectedCompany.nama_kontak_person || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Company</DialogTitle>
              <p className="text-sm text-slate-600">Update data company</p>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_nama_perusahaan">Nama Company *</Label>
                  <Input
                    id="edit_nama_perusahaan"
                    value={formData.nama_perusahaan}
                    onChange={(e) => setFormData({ ...formData, nama_perusahaan: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_email">Email *</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_alamat">Alamat Lengkap *</Label>
                <textarea
                  id="edit_alamat"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.alamat_lengkap}
                  onChange={(e) => setFormData({ ...formData, alamat_lengkap: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_npwp">Nomor NPWP *</Label>
                  <Input
                    id="edit_npwp"
                    value={formData.nomor_npwp}
                    onChange={(e) => setFormData({ ...formData, nomor_npwp: e.target.value })}
                    required
                  />
                </div>
                <SearchableBankSelect
                  value={formData.nama_bank}
                  onChange={(bankName) => setFormData({ ...formData, nama_bank: bankName })}
                  label="Nama Bank"
                  required={true}
                  id="edit_bank"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_rekening">Nomor Rekening *</Label>
                  <Input
                    id="edit_rekening"
                    value={formData.nomor_rekening}
                    onChange={(e) => setFormData({ ...formData, nomor_rekening: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_pemilik">Nama Pemilik Rekening *</Label>
                  <Input
                    id="edit_pemilik"
                    value={formData.nama_pemilik_rekening}
                    onChange={(e) => setFormData({ ...formData, nama_pemilik_rekening: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_nomor_kontak">Nomor Kontak Person * (10-13 digit)</Label>
                  <Input
                    id="edit_nomor_kontak"
                    type="tel"
                    pattern="[0-9]{10,13}"
                    maxLength="13"
                    value={formData.nomor_kontak_person}
                    onChange={(e) => setFormData({ ...formData, nomor_kontak_person: e.target.value.replace(/[^0-9]/g, '') })}
                    placeholder="08123456789"
                    required
                  />
                  <p className="text-xs text-slate-500">Min 10 digit, Max 13 digit</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_nama_kontak">Nama Kontak Person *</Label>
                  <Input
                    id="edit_nama_kontak"
                    type="text"
                    value={formData.nama_kontak_person}
                    onChange={(e) => setFormData({ ...formData, nama_kontak_person: e.target.value })}
                    placeholder="John Doe"
                    required
                  />
                </div>
              </div>


                <div className="space-y-3 border rounded-lg p-4 bg-slate-50">
                  <div>
                    <p className="font-medium text-slate-800">Penanda Tangan Cetakan</p>
                    <p className="text-xs text-slate-500">
                      Nama ini dibaca langsung dari master saat mencetak, sehingga pergantian pejabat
                      langsung berlaku — termasuk pada dokumen lama yang dicetak ulang.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="edit_ttd_fat_nama">FAT Manager — Nama</Label>
                      <Input
                        id="edit_ttd_fat_nama"
                        value={formData.ttd_fat_nama}
                        onChange={(e) => updateFormField('ttd_fat_nama', e.target.value)}
                        placeholder="Kosongkan bila cukup jabatan"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="edit_ttd_fat_jabatan">FAT Manager — Jabatan</Label>
                      <Input
                        id="edit_ttd_fat_jabatan"
                        value={formData.ttd_fat_jabatan}
                        onChange={(e) => updateFormField('ttd_fat_jabatan', e.target.value)}
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-500 -mt-1">Cetak PO kolom “Diketahui Oleh”, Cetak Invoice kolom “Disetujui Oleh”</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="edit_ttd_gm_nama">General Manager — Nama</Label>
                      <Input
                        id="edit_ttd_gm_nama"
                        value={formData.ttd_gm_nama}
                        onChange={(e) => updateFormField('ttd_gm_nama', e.target.value)}
                        placeholder="Kosongkan bila cukup jabatan"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="edit_ttd_gm_jabatan">General Manager — Jabatan</Label>
                      <Input
                        id="edit_ttd_gm_jabatan"
                        value={formData.ttd_gm_jabatan}
                        onChange={(e) => updateFormField('ttd_gm_jabatan', e.target.value)}
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-500 -mt-1">Cetak PO kolom “Disetujui Oleh”</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="edit_ttd_pemeriksa_nama">Pemeriksa — Nama</Label>
                      <Input
                        id="edit_ttd_pemeriksa_nama"
                        value={formData.ttd_pemeriksa_nama}
                        onChange={(e) => updateFormField('ttd_pemeriksa_nama', e.target.value)}
                        placeholder="Kosongkan bila cukup jabatan"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="edit_ttd_pemeriksa_jabatan">Pemeriksa — Jabatan</Label>
                      <Input
                        id="edit_ttd_pemeriksa_jabatan"
                        value={formData.ttd_pemeriksa_jabatan}
                        onChange={(e) => updateFormField('ttd_pemeriksa_jabatan', e.target.value)}
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-500 -mt-1">Voucher Pembayaran kolom “Diperiksa Oleh”</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="edit_ttd_finance_nama">Finance — Nama</Label>
                      <Input
                        id="edit_ttd_finance_nama"
                        value={formData.ttd_finance_nama}
                        onChange={(e) => updateFormField('ttd_finance_nama', e.target.value)}
                        placeholder="Kosongkan bila cukup jabatan"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="edit_ttd_finance_jabatan">Finance — Jabatan</Label>
                      <Input
                        id="edit_ttd_finance_jabatan"
                        value={formData.ttd_finance_jabatan}
                        onChange={(e) => updateFormField('ttd_finance_jabatan', e.target.value)}
                      />
                    </div>
                    <p className="col-span-2 text-xs text-slate-500 -mt-1">Voucher Pembayaran kolom “Dibayar Oleh”</p>
                  </div>
                </div>

                <CategoryPicker value={formData.category} onChange={(c) => setFormData({ ...formData, category: c })} locked={lockedCat} testId="edit-company-category" />
                <p className="text-xs text-slate-500 -mt-2">Kategori menentukan perusahaan muncul di form IPR atau OPR. PO tetap bisa memakai semua perusahaan.</p>

              <Button type="submit" className="w-full" data-testid="update-company-button">
                Update Company
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}



